import { z } from 'zod'
import {
  FURNITURE_MODELS,
  GEOMETRY_VERSION,
  geometryBounds,
  pointInPolygon,
  type FurnitureModel,
  type GeometryBox,
  type LocationGeometry,
  type PlanPoint,
} from './geometry'

/**
 * Catalogue de meubles (site, appli, API) : modèles courants avec leurs dimensions, leur dessin 3D et leur
 * intérieur (tiroirs, étagères, cases), rangés par pièce. Les noms sont traduits côté écran
 * (`catalog.items.<id>`, `catalog.parts.<part>`) : le catalogue lui-même ne contient pas de phrases. Aucun nom de
 * marque. « Mes modèles » (meubles enregistrés par le foyer) suivent le même format (`FurnitureSpec`).
 */

/** Pièces du catalogue (rubriques). */
export const CATALOG_ROOMS = ['bedroom', 'living', 'kitchen', 'bathroom', 'office', 'entrance', 'garage'] as const
export type CatalogRoom = (typeof CATALOG_ROOMS)[number]

/** Parties d'un intérieur en série (nom de chaque rangement créé : « Tiroir 1 », « Étagère 2 »…). */
export const INTERIOR_PARTS = ['drawer', 'shelf', 'compartment', 'basket'] as const
export type InteriorPart = (typeof INTERIOR_PARTS)[number]

/** Intérieur d'un modèle du catalogue : une série de parties, ou une grille de cases (A1, A2… B1…). */
export type CatalogInterior = { kind: 'series'; part: InteriorPart; count: number } | { kind: 'grid'; rows: number; cols: number }

export type CatalogItem = {
  id: string
  room: CatalogRoom
  model: FurnitureModel
  /** Dimensions en mètres : largeur, profondeur, hauteur ; `elevation` : hauteur du bas (meuble suspendu). */
  width: number
  depth: number
  height: number
  elevation?: number
  interior?: CatalogInterior
}

const s = (part: InteriorPart, count: number): CatalogInterior => ({ kind: 'series', part, count })
const g = (rows: number, cols: number): CatalogInterior => ({ kind: 'grid', rows, cols })

export const FURNITURE_CATALOG: readonly CatalogItem[] = [
  // Chambre
  { id: 'bed-90', room: 'bedroom', model: 'bed', width: 0.9, depth: 1.9, height: 0.5 },
  { id: 'bed-140', room: 'bedroom', model: 'bed', width: 1.4, depth: 1.9, height: 0.5 },
  { id: 'bed-160', room: 'bedroom', model: 'bed', width: 1.6, depth: 2, height: 0.5 },
  { id: 'wardrobe-2', room: 'bedroom', model: 'wardrobe', width: 1, depth: 0.6, height: 2, interior: s('shelf', 4) },
  { id: 'wardrobe-3', room: 'bedroom', model: 'wardrobe', width: 1.5, depth: 0.6, height: 2, interior: s('shelf', 5) },
  { id: 'chest-4', room: 'bedroom', model: 'drawers', width: 0.8, depth: 0.45, height: 0.85, interior: s('drawer', 4) },
  { id: 'chest-6', room: 'bedroom', model: 'drawers', width: 1.2, depth: 0.45, height: 0.85, interior: s('drawer', 6) },
  { id: 'nightstand', room: 'bedroom', model: 'drawers', width: 0.45, depth: 0.4, height: 0.55, interior: s('drawer', 2) },
  // Séjour
  { id: 'sofa-2', room: 'living', model: 'sofa', width: 1.6, depth: 0.9, height: 0.85 },
  { id: 'sofa-3', room: 'living', model: 'sofa', width: 2.1, depth: 0.9, height: 0.85 },
  { id: 'tv-unit', room: 'living', model: 'cabinet', width: 1.6, depth: 0.45, height: 0.5, interior: s('compartment', 2) },
  { id: 'sideboard', room: 'living', model: 'cabinet', width: 1.8, depth: 0.45, height: 0.8, interior: s('compartment', 3) },
  { id: 'bookcase', room: 'living', model: 'shelf', width: 0.8, depth: 0.3, height: 2, interior: s('shelf', 5) },
  { id: 'cube-2x4', room: 'living', model: 'shelf', width: 0.77, depth: 0.39, height: 1.47, interior: g(4, 2) },
  { id: 'cube-4x4', room: 'living', model: 'shelf', width: 1.47, depth: 0.39, height: 1.47, interior: g(4, 4) },
  { id: 'coffee-table', room: 'living', model: 'table', width: 1.1, depth: 0.6, height: 0.45 },
  { id: 'dining-table', room: 'living', model: 'table', width: 1.6, depth: 0.9, height: 0.75 },
  { id: 'plant', room: 'living', model: 'plant', width: 0.5, depth: 0.5, height: 1.2 },
  // Cuisine
  { id: 'fridge', room: 'kitchen', model: 'fridge', width: 0.6, depth: 0.65, height: 1.85, interior: s('shelf', 4) },
  { id: 'base-cabinet-60', room: 'kitchen', model: 'counter', width: 0.6, depth: 0.6, height: 0.9, interior: s('drawer', 3) },
  { id: 'base-cabinet-120', room: 'kitchen', model: 'counter', width: 1.2, depth: 0.6, height: 0.9, interior: s('compartment', 2) },
  { id: 'wall-cabinet', room: 'kitchen', model: 'cabinet', width: 0.8, depth: 0.35, height: 0.7, elevation: 1.4, interior: s('shelf', 2) },
  { id: 'pantry', room: 'kitchen', model: 'cabinet', width: 0.6, depth: 0.6, height: 2.1, interior: s('shelf', 5) },
  // Salle de bain
  { id: 'vanity-80', room: 'bathroom', model: 'vanity', width: 0.8, depth: 0.5, height: 0.85, interior: s('drawer', 2) },
  { id: 'mirror-cabinet', room: 'bathroom', model: 'cabinet', width: 0.6, depth: 0.15, height: 0.7, elevation: 1.3, interior: s('shelf', 2) },
  { id: 'bathroom-column', room: 'bathroom', model: 'cabinet', width: 0.35, depth: 0.35, height: 1.7, interior: s('shelf', 4) },
  { id: 'washing-machine', room: 'bathroom', model: 'box', width: 0.6, depth: 0.6, height: 0.85 },
  // Bureau
  { id: 'desk', room: 'office', model: 'table', width: 1.2, depth: 0.6, height: 0.75 },
  { id: 'drawer-unit', room: 'office', model: 'drawers', width: 0.4, depth: 0.5, height: 0.6, interior: s('drawer', 3) },
  { id: 'office-shelf', room: 'office', model: 'shelf', width: 0.8, depth: 0.4, height: 1.8, interior: s('shelf', 5) },
  // Entrée
  { id: 'shoe-cabinet', room: 'entrance', model: 'cabinet', width: 0.8, depth: 0.3, height: 1, interior: s('compartment', 3) },
  { id: 'entry-chest', room: 'entrance', model: 'drawers', width: 0.8, depth: 0.35, height: 0.8, interior: s('drawer', 3) },
  // Garage et jardin
  { id: 'workbench', room: 'garage', model: 'table', width: 1.8, depth: 0.7, height: 0.9, interior: s('drawer', 3) },
  { id: 'metal-shelf', room: 'garage', model: 'shelf', width: 0.9, depth: 0.4, height: 1.8, interior: s('shelf', 5) },
  { id: 'tool-cabinet', room: 'garage', model: 'cabinet', width: 0.8, depth: 0.45, height: 1.8, interior: s('shelf', 4) },
  { id: 'storage-bins', room: 'garage', model: 'shelf', width: 1, depth: 0.45, height: 1.2, interior: s('basket', 6) },
  { id: 'garden-chest', room: 'garage', model: 'box', width: 1.3, depth: 0.55, height: 0.6 },
  { id: 'kennel', room: 'garage', model: 'box', width: 0.8, depth: 1, height: 0.8 },
]

export function catalogItem(id: string): CatalogItem | undefined {
  return FURNITURE_CATALOG.find((i) => i.id === id)
}

// --- Format commun : catalogue et « mes modèles » -------------------------------------------------------

/** Intérieur tel que l'API le crée : série nommée (« Tiroir » → Tiroir 1…), ou grille de cases. */
export const FurnitureInteriorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('series'), prefix: z.string().trim().min(1).max(40), count: z.number().int().min(1).max(50) }),
  z
    .object({
      kind: z.literal('grid'),
      rows: z.number().int().min(1).max(20),
      cols: z.number().int().min(1).max(20),
      prefix: z.string().trim().max(40).optional(),
    })
    .refine((v) => v.rows * v.cols <= 100, { message: 'batch_too_large' }),
])
export type FurnitureInterior = z.infer<typeof FurnitureInteriorSchema>

/** Ce qu'il faut pour créer un meuble « tout prêt » : son dessin, sa taille, son intérieur. */
export const FurnitureSpecSchema = z.object({
  model: z.enum(FURNITURE_MODELS),
  width: z.number().min(0.1).max(20),
  depth: z.number().min(0.1).max(20),
  height: z.number().min(0.1).max(10),
  elevation: z.number().min(0).max(10).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  interior: FurnitureInteriorSchema.nullable().optional(),
})
export type FurnitureSpec = z.infer<typeof FurnitureSpecSchema>

/** Modèle du catalogue → format commun, avec les noms des parties traduits par l'écran (`partName`). */
export function catalogSpec(item: CatalogItem, partName: (part: InteriorPart) => string): FurnitureSpec {
  const interior: FurnitureInterior | null = !item.interior
    ? null
    : item.interior.kind === 'grid'
      ? { kind: 'grid', rows: item.interior.rows, cols: item.interior.cols }
      : { kind: 'series', prefix: partName(item.interior.part), count: item.interior.count }
  return {
    model: item.model,
    width: item.width,
    depth: item.depth,
    height: item.height,
    ...(item.elevation ? { elevation: item.elevation } : {}),
    interior,
  }
}

/** Nombre de rangements créés pour l'intérieur (limites de l'offre). */
export function interiorCount(interior: FurnitureInterior | null | undefined): number {
  if (!interior) return 0
  return interior.kind === 'grid' ? interior.rows * interior.cols : interior.count
}

/**
 * Géométrie d'un meuble créé depuis un modèle : centré sur `at` s'il est donné (clic dans la vue 3D), sinon posé
 * dans le coin de sa pièce (à 40 cm des murs), sinon à l'origine du lieu.
 */
export function specGeometry(
  spec: FurnitureSpec,
  at: { x: number; z: number; rotationY?: number } | null | undefined,
  room: LocationGeometry | null | undefined,
): GeometryBox {
  const corner = room ? geometryBounds(room) : null
  const x = at ? at.x - spec.width / 2 : (corner?.minX ?? 0) + (corner ? 0.4 : 0)
  const z = at ? at.z - spec.depth / 2 : (corner?.minZ ?? 0) + (corner ? 0.4 : 0)
  return {
    version: GEOMETRY_VERSION,
    kind: 'box',
    x: round(x),
    y: spec.elevation ?? 0,
    z: round(z),
    width: spec.width,
    height: spec.height,
    depth: spec.depth,
    rotationY: at?.rotationY ?? 0,
    model: spec.model,
    ...(spec.color ? { color: spec.color } : {}),
  }
}

/**
 * Modèle tiré d'un meuble existant (« Enregistrer comme modèle ») : son dessin, sa taille et son intérieur
 * (grille si tous ses rangements ont une case, sinon série nommée d'après le premier, chiffres de fin retirés).
 */
export function specFromFurniture(
  furniture: { name: string; geometry: LocationGeometry | null },
  children: { name: string; gridRow: number | null; gridCol: number | null }[],
  guessModel: (name: string) => FurnitureModel,
): FurnitureSpec {
  const geo = furniture.geometry?.kind === 'box' ? furniture.geometry : null
  let interior: FurnitureInterior | null = null
  if (children.length) {
    const grid = children.every((c) => c.gridRow != null && c.gridCol != null)
    if (grid) {
      interior = {
        kind: 'grid',
        rows: Math.max(...children.map((c) => c.gridRow!)) + 1,
        cols: Math.max(...children.map((c) => c.gridCol!)) + 1,
      }
    } else {
      const prefix = children[0]!.name.replace(/\s*[0-9A-Z]{1,3}$/u, '').trim() || children[0]!.name
      interior = { kind: 'series', prefix: prefix.slice(0, 40), count: Math.min(children.length, 50) }
    }
  }
  return {
    model: geo?.model ?? guessModel(furniture.name),
    width: geo?.width ?? 1,
    depth: geo?.depth ?? 0.5,
    height: geo?.height ?? 1,
    ...(geo?.y ? { elevation: geo.y } : {}),
    ...(geo?.color ? { color: geo.color } : {}),
    interior,
  }
}

const round = (n: number) => Math.round(n * 1000) / 1000

// --- Finitions ----------------------------------------------------------------------------------------------

/**
 * Finitions proposées au choix d'un modèle (couleur du meuble dans la vue 3D) ; `natural` garde la couleur propre au
 * dessin du meuble. Noms traduits : `catalog.finishes.<id>`.
 */
export const FURNITURE_FINISHES = [
  { id: 'natural', color: null },
  { id: 'oak', color: '#c9a57a' },
  { id: 'walnut', color: '#7a5233' },
  { id: 'white', color: '#eeebe4' },
  { id: 'black', color: '#2e2e30' },
  { id: 'grey', color: '#9b9a96' },
  { id: 'sage', color: '#9fb39a' },
  { id: 'blue', color: '#5b7c99' },
  { id: 'terracotta', color: '#b4471e' },
] as const
export type FurnitureFinish = (typeof FURNITURE_FINISHES)[number]['id']

/** Le modèle avec la finition choisie (`natural` : sans couleur imposée). */
export function withFinish(spec: FurnitureSpec, finish: FurnitureFinish): FurnitureSpec {
  const color = FURNITURE_FINISHES.find((f) => f.id === finish)?.color ?? null
  const { color: _old, ...rest } = spec
  return color ? { ...rest, color } : rest
}

/** Finition correspondant à la couleur d'un modèle (`natural` si aucune ou inconnue). */
export function finishOf(spec: FurnitureSpec): FurnitureFinish {
  return FURNITURE_FINISHES.find((f) => f.color && f.color === spec.color?.toLowerCase())?.id ?? 'natural'
}

// --- Contre le mur ----------------------------------------------------------------------------------------

/** Épaisseur des murs déduits (voir `roomWalls`) : le meuble se pose contre leur face intérieure. */
const WALL_THICKNESS = 0.1

/**
 * Pose « contre le mur le plus proche » : si le clic est à moins de `maxDistance` mètres d'un mur de la pièce
 * (`outline`, son contour au sol), le meuble y est adossé (son dos, côté −z local, contre la face intérieure du mur),
 * tourné parallèlement au mur et gardé entre ses extrémités. `null` si aucun mur n'est assez proche.
 */
export function snapToWall(
  size: { width: number; depth: number },
  at: { x: number; z: number },
  outline: PlanPoint[],
  maxDistance = 1,
): { x: number; z: number; rotationY: number } | null {
  let best: { d: number; ax: number; az: number; ux: number; uz: number; len: number; t: number } | null = null
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!
    const b = outline[(i + 1) % outline.length]!
    const len = Math.hypot(b.x - a.x, b.z - a.z)
    if (len < 1e-6) continue
    const ux = (b.x - a.x) / len
    const uz = (b.z - a.z) / len
    const t = Math.min(len, Math.max(0, (at.x - a.x) * ux + (at.z - a.z) * uz))
    const d = Math.hypot(at.x - (a.x + ux * t), at.z - (a.z + uz * t))
    if (d <= maxDistance && (!best || d < best.d)) best = { d, ax: a.x, az: a.z, ux, uz, len, t }
  }
  if (!best) return null
  const { ax, az, ux, uz, len } = best
  // Normale vers l'extérieur de la pièce : celle dont le côté opposé est à l'intérieur.
  let nx = uz
  let nz = -ux
  const mid = { x: ax + ux * best.t, z: az + uz * best.t }
  if (!pointInPolygon({ x: mid.x - nx * 0.05, z: mid.z - nz * 0.05 }, outline)) {
    nx = -nx
    nz = -nz
  }
  // Le meuble reste entre les extrémités du mur (centré si le mur est plus court que lui).
  // (sans mordre sur l'épaisseur des murs voisins dans un coin).
  const half = size.width / 2 + WALL_THICKNESS / 2
  const t = len <= 2 * half ? len / 2 : Math.min(len - half, Math.max(half, best.t))
  const back = WALL_THICKNESS / 2 + size.depth / 2
  const deg = (Math.atan2(-nx, -nz) * 180) / Math.PI
  return {
    x: round(ax + ux * t - nx * back),
    z: round(az + uz * t - nz * back),
    rotationY: ((Math.round(deg) % 360) + 360) % 360,
  }
}

// --- Affichage (site et appli) : `t` est la fonction de traduction de l'écran ---------------------------

type Translate = (key: string, options?: Record<string, unknown>) => string

/** Un modèle choisi (catalogue ou « mes modèles ») : nom proposé et description du meuble à créer. */
export type CatalogPick = { name: string; spec: FurnitureSpec }

/**
 * Meuble posé qu'on fait glisser : coin min amené en (x, z), au pas de 5 cm ; si `outline` (contour de sa pièce) est
 * donné et qu'un mur est à moins d'1 m de son centre, il s'y adosse face à la pièce (`snapToWall`).
 */
export function dragFurnitureTo(start: GeometryBox, x: number, z: number, outline?: PlanPoint[] | null): GeometryBox {
  const snapped = outline ? snapToWall(start, { x: x + start.width / 2, z: z + start.depth / 2 }, outline) : null
  if (!snapped) return { ...start, x: Math.round(x * 20) / 20, z: Math.round(z * 20) / 20 }
  return { ...start, x: round(snapped.x - start.width / 2), z: round(snapped.z - start.depth / 2), rotationY: snapped.rotationY }
}

/** Modèle du catalogue → choix, avec son nom et les noms de ses parties dans la langue de l'écran. */
export function catalogPick(t: Translate, item: CatalogItem): CatalogPick {
  return { name: t(`catalog.items.${item.id}`), spec: catalogSpec(item, (part) => t(`catalog.parts.${part}`)) }
}

/** « 80 × 45 cm, 85 cm de haut · avec 4 tiroirs » (pluriel juste pour un modèle du catalogue). */
export function describeFurnitureSpec(t: Translate, spec: FurnitureSpec, item?: CatalogItem): string {
  const cm = (m: number) => Math.round(m * 100)
  const size = t('catalog.size', { w: cm(spec.width), d: cm(spec.depth), h: cm(spec.height) })
  const interior = spec.interior
  if (!interior) return size
  if (interior.kind === 'grid') return `${size} · ${t('catalog.interiorGrid', { rows: interior.rows, cols: interior.cols })}`
  const what =
    item?.interior?.kind === 'series'
      ? t(`catalog.partCount.${item.interior.part}`, { count: interior.count })
      : `${interior.count} × ${interior.prefix}`
  return `${size} · ${t('catalog.withParts', { what })}`
}

// --- Routes de l'API ---------------------------------------------------------------------------------------

/** `POST …/locations/furniture` : un meuble tout prêt (dessin, taille, intérieur), idempotent par `id`. */
export const CreateFurnitureBodySchema = z.object({
  id: z.string().uuid().optional(),
  parentId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  spec: FurnitureSpecSchema,
  /** Centre du meuble dans le repère du lieu (clic dans la vue 3D) ; sinon coin de la pièce. */
  at: z.object({ x: z.number().finite(), z: z.number().finite(), rotationY: z.number().finite().optional() }).optional(),
})
export type CreateFurnitureBody = z.infer<typeof CreateFurnitureBodySchema>

/** Modèle de meuble du foyer (« mes modèles »). */
export const TEMPLATE_SCOPES = ['household', 'personal'] as const
export type TemplateScope = (typeof TEMPLATE_SCOPES)[number]

export const FurnitureTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  spec: FurnitureSpecSchema,
  /** `household` : modèle du foyer (tous ses membres) ; `personal` : le mien, proposé dans tous mes foyers. */
  scope: z.enum(TEMPLATE_SCOPES),
  createdAt: z.string(),
})
export type FurnitureTemplate = z.infer<typeof FurnitureTemplateSchema>

/** Enregistrer un modèle : à partir d'un meuble du foyer (`fromLocationId`), ou d'une description (`spec`). */
export const CreateFurnitureTemplateBodySchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    fromLocationId: z.string().uuid().optional(),
    spec: FurnitureSpecSchema.optional(),
    /** `personal` : gardé pour moi, dans tous mes foyers (par défaut : modèle du foyer). */
    scope: z.enum(TEMPLATE_SCOPES).optional(),
  })
  .refine((b) => Boolean(b.fromLocationId) !== Boolean(b.spec), { message: 'one_of_fromLocationId_spec' })
  .refine((b) => Boolean(b.fromLocationId) || Boolean(b.name), { message: 'name_required' })
export type CreateFurnitureTemplateBody = z.infer<typeof CreateFurnitureTemplateBodySchema>
